import React, { useEffect, useRef, useState } from 'react'

import { ChevronLeftIcon, ChevronRightIcon, PlayIcon } from 'lucide-react';
import { Swiper, SwiperSlide, type SwiperClass } from 'swiper/react'

import { Box, IconButton, styled, Zoom, type SxProps, type Theme } from '@mui/material'
import { useTheme } from '@mui/material/styles';

import { Image, Skeleton, mediaPlateColor } from '@repo/ui'
import { isVideoUrl, productMediaKindOf } from '@repo/types'


import 'swiper/scss'

const StyledWrapper = styled(Box)(({ theme, height }) => ({
  '.swiper': {
    border: `1px solid ${theme.palette.grey[100]}`,
    borderRadius: '12px'
  },
  
  '.swiper-wrapper': {
    alignItems: 'center',
  },
  
  '.swiper-slide': {
    textAlign: 'center',
    height: height,
  },
}))

const StyledThumbWrapper = styled(Box)(() => ({
  position: 'relative',
  padding: '0 0px',
  marginTop: '16px',
}))

interface SlideshowProps {
  /** null = still loading (shows the skeleton); [] = loaded but no images. */
  images: string[] | null
  /**
   * Which image to open on. Defaults to the first.
   *
   * Exists so a gallery opened FROM a specific thumbnail starts on that image rather than
   * resetting to the top of the list — clicking the third attachment and landing on the
   * first reads as the click having missed.
   */
  initialIndex?: number
  /** Fill the available height instead of the product card's square. */
  fill?: boolean
  /**
   * URL of an image the gallery should jump to whenever this value changes.
   *
   * Exists so a parent can surface a selection-specific image the moment the
   * selection changes — the tag shop swaps the packaging shot as you pick a
   * quantity, and without this the array changes underneath a viewer still
   * parked on slide one, so the change is invisible (VRG-266).
   *
   * Deliberately a URL rather than an index: the caller knows which IMAGE it
   * cares about, not where it landed in the array after merging.
   */
  focusImage?: string | null
  /**
   * Show the thumbnail strip beneath the image. Turn it OFF for compact,
   * fixed-height uses — a picker row, a list cell — where the strip would make
   * the row's height depend on how many images that product happens to have,
   * so a one-image row and a five-image row no longer line up.
   *
   * The set stays navigable without it: the hover arrows are unaffected, and a
   * position pill replaces the strip as the "there are more of these" signal.
   */
  showThumbs?: boolean
  /**
   * Paint the constant PRODUCT MEDIA PLATE behind the image (VRG-749 follow-up).
   *
   * Off by default, and opt-in rather than always-on because this component also
   * carries things that are not product media — `ImageGalleryModal` opens it over
   * ATTACHMENTS, where a light plate behind a dark screenshot would be wrong. The
   * opt-in is safe here precisely because the wrong answer is visible on the
   * surface you are already looking at, unlike the referrer prop (§42) whose
   * omission showed up nowhere.
   *
   * Pass it wherever this renders a PRODUCT, so a product photograph and a
   * `ProductModel` land on the same surface. Live on the product detail page; the
   * certificate page's Product tab, the product picker and the tag-shop gallery
   * are the same case and have not been converted — see the task file.
   *
   * Invisible unless the artwork has transparency: the non-`fill` slide is
   * `cover`, so an opaque photograph covers the plate entirely.
   */
  mediaPlate?: boolean
  /**
   * How each slide's artwork fills its frame.
   *
   * `cover` (the default) crops to the square, which is right for an opaque product
   * PHOTOGRAPH — the tiles stay flush and nothing shows through. It is wrong for a
   * cut-out on transparency: a tag plate IS the part, centred on empty space, so
   * `cover` zooms past the part's own edges and crops them away. The tag-shop
   * gallery shipped exactly that — an 85.6x54mm card rendered as a wall of black
   * with two letters of the wordmark left in frame (VRG-780).
   *
   * Pair `contain` with `mediaPlate`: once the artwork no longer fills the frame,
   * something has to paint what is around it, and the plate is that surface.
   */
  fit?: 'cover' | 'contain'
  /**
   * A node rendered as slide 0, AHEAD of `images`.
   *
   * Exists because a tag's first picture is not a picture: it is the rotatable
   * model, and the stills are the views you get after it. Passing it as a node
   * rather than widening `images` to a union keeps every other caller's contract
   * exactly as it was — `images` is still a list of URLs — and keeps the viewer's
   * props (spin, reset, load state) in the hands of the surface that owns it.
   *
   * `leadThumb` is required alongside it in practice: the strip renders URLs — an
   * `<Image>`, or since VRG-803 a `<video>` frame for a clip — and cannot render a
   * node, so the model needs a still to stand for it there.
   */
  leadSlide?: React.ReactNode
  /** The thumbnail that stands for `leadSlide` in the strip. */
  leadThumb?: string
}

/**
 * VRG-803 — an entry of `products.images` that must be PLAYED rather than shown.
 *
 * `images` is a flat list of URL strings with no type discriminator, and since VRG-777 a
 * clip may sit anywhere in it. Every entry used to go into an `<Image>`, which is an
 * `<img>`: it cannot decode an mp4, so it fired `onError`, fell to
 * `ProductImagePlaceholder`, and a product with a video showed the empty-bag glyph where
 * its hero should be — on the product page, in the gallery modal, and in the picker.
 *
 * The predicate comes from `@repo/types` and is deliberately NOT re-derived here. The
 * module that owns it records why: an independently-written copy of the accept list
 * advertised HEIC for months while the API refused it, so the one list is shared.
 *
 * `productMediaKindOf` rather than `isVideoUrl` for the SLIDE, per its own docblock —
 * this is the case where rendering nothing is right for an unrecognised value, because
 * mounting an empty `<video>` leaves the viewer looking at a black box. A URL with no
 * recognised extension (a Shopify CDN path, an imported scrape) therefore still goes to
 * the `<img>`, which is the common case and the one that must not regress.
 */
const VideoSlide = ({ src, fit }: { src: string; fit: 'cover' | 'contain' }) => (
  <video
    src={src}
    /*
     * `muted` is not a preference, it is the price of admission: browsers autoplay only
     * muted inline video. `playsInline` keeps Safari from going fullscreen. Both are
     * carried over from `apps/app`'s ImageCarousel, which argues them at length — the two
     * surfaces should not disagree about what playing a product clip means.
     */
    autoPlay
    muted
    loop
    playsInline
    style={{
      width: '100%',
      height: '100%',
      objectFit: fit,
      display: 'block',
      /*
       * A letterboxed portrait clip under `contain` would otherwise show the wrapper's
       * own surface at the bars, which reads as a rendering fault rather than as
       * letterboxing.
       */
      background: '#000',
    }}
  />
)

/**
 * VRG-803 — the same entry in the thumbnail strip.
 *
 * A STILL frame of the clip itself, not a playing one: a strip of looping videos is both
 * noisy and a decode per thumbnail, and the strip's job is "which slide is this", not
 * "watch this". `#t=0.1` is what makes the frame appear — `preload="metadata"` alone
 * leaves several browsers painting an empty box until something forces a decode, and a
 * media fragment is never sent to the server, so it is safe on a signed URL.
 *
 * ⚠️ NOT `getProductThumbnail(images)`, which returns the first non-video still. That is
 * right for a HERO poster (ImageCarousel uses it as one) and wrong here: thumbnail N
 * would then show the same picture as thumbnail 1, so a product with one still and one
 * clip renders a strip of two identical images and the clip is undiscoverable. The frame
 * a thumbnail shows has to come from the slide it selects.
 */
const VideoThumb = ({ src }: { src: string }) => (
  <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 2 }}>
    <video
      src={`${src}#t=0.1`}
      muted
      playsInline
      preload="metadata"
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', background: '#000' }}
    />
    {/* The one thing a still frame cannot say for itself. Without it a video thumbnail is
        indistinguishable from a photograph, which is the state this ticket started in. */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PlayIcon size={12} strokeWidth={1.5} color="#fff" fill="#fff" />
      </Box>
    </Box>
  </Box>
)

export const Slideshow = (props: SlideshowProps) => {
  const {
    images = null,
    initialIndex = 0,
    fill = false,
    focusImage = null,
    showThumbs = true,
    mediaPlate = false,
    fit = 'cover',
    leadSlide = null,
    leadThumb,
  } = props
  const [showOpenButton, setShowOpenButton] = useState(false);
  const theme = useTheme()


  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  const [swiperThumbs, setSwiperThumbs] = useState<SwiperClass | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex)

  const handleNext = () => {
    swiper?.slideNext()
  }

  const handlePrev = () => {
    swiper?.slidePrev()
  }

  const onSlideChange = (swiper: SwiperClass) => {
    swiperThumbs?.slideToLoop(swiper.realIndex)
    setActiveIndex(swiper.realIndex)
  }

  /*
   * VRG-803 — only the slide you are looking at plays.
   *
   * Every slide is MOUNTED, not just the visible one, so a product with four clips would
   * otherwise decode four 1080p streams at once behind a single visible frame — on a dense
   * admin page that is doing nothing for anybody. `apps/app`'s ImageCarousel reached the
   * same conclusion and pauses its off-slides for the same reason.
   *
   * Done against the DOM rather than through refs on the element, deliberately: `loop` is
   * on, so Swiper CLONES slides, and a cloned node is a copy React never rendered and
   * cannot hold a ref to. Reading `.swiper-slide-active` is reading the same thing Swiper
   * itself uses to decide what is on screen, clones included.
   *
   * Conservative by construction: a video whose slide cannot be identified is LEFT ALONE,
   * so the worst this can do if Swiper's class names ever change is fall back to the old
   * behaviour of everything playing. The elements keep their own `autoPlay` for the same
   * reason — if this effect never runs, the hero still plays.
   */
  useEffect(() => {
    const root = swiper?.el
    if (!root) return
    for (const video of Array.from(root.querySelectorAll('video'))) {
      const slide = video.closest('.swiper-slide')
      if (!slide) continue
      if (slide.classList.contains('swiper-slide-active')) {
        // `play()` rejects when the browser declines the gesture; nothing to do about it
        // here, and an unhandled rejection in a carousel is noise in the console.
        void video.play().catch(() => {})
      } else if (!video.paused) {
        video.pause()
      }
    }
  }, [swiper, activeIndex, images])
  const onClickThumb = (index: number) => {
    swiper?.slideToLoop(index)
  }

  /*
   * The focus image as last ACTED ON, so the effect can tell a change from a first
   * sighting. `focusImage` is documented as "jump when this value CHANGES", and
   * without this ref it also jumps on mount — harmless while slide 0 was the hero
   * (the jump was to the slide already showing), and not harmless once a `leadSlide`
   * sits in front of it: the gallery opened on the model and was immediately slid off
   * it onto the still, which looks like the model failed rather than like a focus
   * jump. Seeded with the initial value, so the first render is a no-op by
   * construction rather than by a flag someone has to clear.
   */
  const lastFocus = useRef<string | null>(focusImage)

  // Jump to the caller's focus image when it changes. Guarded on the image actually
  // being present, so a focusImage that is not in `images` is a no-op rather than a
  // slide to index -1.
  useEffect(() => {
    if (focusImage === lastFocus.current) return
    /*
     * ⚠️ THE FIRST NON-NULL VALUE IS NOT A CHANGE, IT IS THE SELECTION ARRIVING.
     *
     * Seeding the ref from the initial prop was not enough: a caller that resolves
     * its selection asynchronously — the tag shop picks a default variant in an
     * effect — mounts with `focusImage` null and sets it a tick later. That reads
     * as a change by every test except intent, so the gallery opened on the model
     * and was then slid onto the still a frame after the page settled, which is
     * exactly the symptom this was supposed to have fixed and is worse for being
     * delayed: it looks like the model loaded and then gave up.
     *
     * So the first value a caller supplies only ARMS the comparison. A real
     * selection change after that still jumps, which is the whole point of the
     * prop — pick a different quantity and the packaging shot comes forward.
     */
    const arming = lastFocus.current == null
    lastFocus.current = focusImage
    if (arming) return
    if (!focusImage || !swiper || !images) return
    const found = images.indexOf(focusImage)
    if (found < 0) return
    /* `images` is offset by one once a lead slide sits in front of it, so the
       swiper index is NOT the array index. Without this the gallery jumps one
       slide short of the image the caller asked for. */
    const index = found + (leadSlide ? 1 : 0)
    if (index !== swiper.realIndex) swiper.slideToLoop(index)
  }, [focusImage, swiper, images, leadSlide])

  if (images == null){
    // Footprint-matched loading skeleton: the square image area + the thumbnail
    // strip, so it occupies the same layout the loaded slideshow will — no layout
    // pop and no grey fallback-image icon flashing in during load (§19).
    //
    // The aspect ratio lives on a WRAPPER, with the Skeleton filling it at height 100%.
    // Putting `aspectRatio` on the Skeleton ITSELF — which is what this did — does not work:
    // MUI's Skeleton root sets `height: '1.2em'` for every variant and only resets it to
    // `auto` for `variant="text"`, so on a `rounded` Skeleton the height is already definite
    // and `aspect-ratio` is ignored. Measured at a 572px column: the square rendered 572x19
    // and each of the five thumbnails 108x19 — the entire gallery placeholder was six
    // hairlines. It reported real widths and a real element count throughout, so no numeric
    // check on width or node count catches it; the thing that collapsed is the one thing
    // footprint parity is about.
    //
    // Reached whenever a caller passes `images` as undefined while its fetch is in flight —
    // today TagsShopProductPage (`images={product?.images}`). ProductPage and the certificate
    // Product tab guard on loaded data and never reach this branch, which is why it went
    // unnoticed on the two surfaces anyone looks at.
    return (
      <>
        <Box sx={{ width: '100%', aspectRatio: '1 / 1' }}>
          <Skeleton variant="rounded" sx={{ width: '100%', height: '100%', borderRadius: '12px' }} />
        </Box>
        {showThumbs && (
          <StyledThumbWrapper>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box key={i} sx={{ flex: 1, aspectRatio: '1 / 1' }}>
                  <Skeleton variant="rounded" sx={{ width: '100%', height: '100%', borderRadius: 2 }} />
                </Box>
              ))}
            </Box>
          </StyledThumbWrapper>
        )}
      </>
    )
  }

  /* The lead slide counts as a slide: a product with a model and no stills is not
     empty, and a product with a model and ONE still has two slides, so it earns the
     arrows and the strip. */
  const slides = images.length + (leadSlide ? 1 : 0)
  const isEmpty = slides === 0
  /* What the strip renders, aligned 1:1 with the slides so `activeIndex` indexes both. */
  const thumbs = leadSlide && leadThumb ? [leadThumb, ...images] : images
  // Arrows key off "is there more than one image"; the STRIP additionally keys off
  // the caller wanting one. Before these were one flag, so hiding the strip would
  // also have taken the arrows with it and left a multi-image set unnavigable.
  const hasMultiple = slides > 1
  const isShowThumbs = hasMultiple && showThumbs

  return (
    <>
      <StyledWrapper
        onMouseEnter={() => setShowOpenButton(true)}
        onMouseLeave={() => setShowOpenButton(false)}
        sx={{
          // `.swiper` is built ONCE rather than spread from two branches. It is the
          // element already carrying the border and the 12px radius, so the plate is
          // clipped by the same corners instead of squaring them off behind the frame
          // — but it is also what `fill` sets a height on, and two sibling spreads
          // each naming `.swiper` would mean the later one silently replaced the
          // earlier one's whole object rather than merging into it. No caller passes
          // both today; this is so that one can.
          '.swiper': {
            ...(mediaPlate ? { backgroundColor: mediaPlateColor(theme) } : {}),
            ...(fill ? { height: '100%' } : {}),
          },
          ...(fill
          // A lightbox should give the image the room the viewport allows, so height is driven
          // by the container and the square is dropped. minHeight 0 lets it shrink inside a
          // flex column instead of forcing the modal past the viewport.
          ? { flex: 1, minHeight: 0, '.swiper-slide': { height: '100%' } }
          : { aspectRatio: '1/1' }),
        }}
        >
        {isEmpty ? (
          // edit this
          <Box sx={{ justifyContent: 'center', border: `1px solid ${theme.palette.primary[200]}`, borderRadius: 3, display: 'flex', minWidth: '100%', aspectRatio: '1/1'}}>
            <Image src="" sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex'}} />
          </Box>
        ) : (
          <>
            <Swiper slidesPerView={1} initialSlide={initialIndex} onSwiper={setSwiper} loop={true} grabCursor={true} onSlideChange={onSlideChange}>
              {hasMultiple && !showThumbs && (
                // Stands in for the strip. Without it a compact slideshow looks
                // like a plain thumbnail and nobody discovers the other images,
                // because the arrows only appear once the cursor is already there.
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 6,
                    right: 6,
                    zIndex: 1,
                    px: 2,
                    py: '2px',
                    borderRadius: 1,
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    color: '#fff',
                    fontSize: 11,
                    lineHeight: 1.5,
                    pointerEvents: 'none',
                  }}
                >
                  {activeIndex + 1} / {slides}
                </Box>
              )}
              {hasMultiple && (
                <>
                <Zoom in={showOpenButton}>
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    height: '28px',
                    width: '28px',
                    marginLeft: 3,
                    zIndex: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.primary[200]}`,
                  }}
                  size="small"
                  onClick={() => handlePrev()}
                  >
                  <ChevronLeftIcon strokeWidth={1} />
                </IconButton>
                </Zoom>
                <Zoom in={showOpenButton}>
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 0,
                    height: '28px',
                    width: '28px',
                    marginRight: 3,
                    zIndex: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.primary[200]}`,
                  }}
                  size="small"
                  onClick={() => handleNext()}
                  >
                  <ChevronRightIcon strokeWidth={1} />
                </IconButton>
                </Zoom>  
                </>
              )}            
              {leadSlide && (
                <SwiperSlide
                  key="lead"
                  style={fill
                    ? { minWidth: '100%', height: '100%' }
                    : { aspectRatio: '1/1', minWidth: '100%' }}
                >
                  {leadSlide}
                </SwiperSlide>
              )}
              {images.map((image: string, index: number) => (
                <SwiperSlide
                  key={index}
                  style={fill
                    ? { minWidth: '100%', height: '100%' }
                    : { aspectRatio: '1/1', minWidth: '100%' }}
                >
                  {/* `contain` in a lightbox: the point of opening an attachment full-size is to
                      read it, and `cover` crops whatever does not match the frame — which for a
                      tall screenshot is most of it. The product card keeps `cover` so its square
                      tiles stay flush. */}
                  {productMediaKindOf(image) === 'video' ? (
                    <VideoSlide src={image} fit={fill ? 'contain' : fit} />
                  ) : (
                    <Image
                      fit={fill ? 'contain' : fit}
                      src={image}
                      sx={{ justifyContent: 'center', display: 'flex', alignItems: 'center', height: '100%' }}
                    />
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
          </>
        )}
      </StyledWrapper>

      {isShowThumbs && (
        <StyledThumbWrapper>
          {/* `auto`-width 64px thumbs in fill mode. The product card's `slidesPerView={5}`
              divides the strip into fifths, which is right when a gallery has five or more
              images but clips them when it has three — each slide is sized to a fifth of the
              much wider lightbox while the image inside keeps its square, so the row ran past
              its edges. Fixed-width slides centre correctly at ANY count. */}
          <Swiper
            slidesPerView={fill ? 'auto' : 5}
            spaceBetween={fill ? 8 : 16}
            centeredSlides={true}
            centeredSlidesBounds={true}
            simulateTouch={true}
            onSwiper={setSwiperThumbs}
          >
            {thumbs.map((image: string, index: number) => (
              <SwiperSlide
                key={index}
                onClick={() => onClickThumb(index)}
                style={fill
                  ? { width: 64, height: 64, flexShrink: 0, display: 'flex' }
                  : { textAlign: 'center', display: 'flex' }}
              >
                {/* ONE style object for both kinds. Written twice, the video thumb and the
                    still would drift on the next border or radius change and the strip
                    would stop reading as one row. */}
                {(() => {
                  const thumbSx: SxProps<Theme> = (theme: Theme) => ({
                    borderRadius: 2,
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: activeIndex === index ? theme.palette.grey[500] : theme.palette.grey[300],
                    cursor: 'pointer',
                    aspectRatio: '1/1',
                    ...(fill && { width: 64, height: 64 }),
                  })
                  return isVideoUrl(image) ? (
                    <Box sx={thumbSx}>
                      <VideoThumb src={image} />
                    </Box>
                  ) : (
                    <Image src={image} fit="cover" sx={thumbSx} />
                  )
                })()}
              </SwiperSlide>
            ))}
          </Swiper>

          {/* <IconButton
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            size="small"
            onClick={() => handlePrev()}
          >
            <ChevronLeftIcon strokeWidth={1} />
          </IconButton>

          <IconButton
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            size="small"
            onClick={() => handleNext()}
          >
            <ChevronRightIcon strokeWidth={1} />
          </IconButton> */}
        </StyledThumbWrapper>
      )}
    </>
  )
}
